import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Euro, TrendingUp, Clock, CheckCircle, Send, CloudOff, Plane, ChevronRight, BarChart3 } from 'lucide-react';
import { expenseService, tripService } from '../services/api';
import SkeletonLoader from '../components/SkeletonLoader';
import useIsMobile from '../hooks/useIsMobile';
import usePendingExpenses from '../hooks/usePendingExpenses';
import { tripDateRange } from '../utils/trips';

const COLORS = ['#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

function Dashboard() {
  const isMobile = useIsMobile();
  const [stats, setStats] = useState(null);
  const [showCharts, setShowCharts] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await expenseService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="hidden md:block text-3xl font-bold text-gray-900">Dashboard</h1>
        
        {/* Stat cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonLoader key={i} variant="stat-card" />
          ))}
        </div>
        
        {/* Charts skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkeletonLoader variant="chart" />
          <SkeletonLoader variant="chart" />
        </div>
      </div>
    );
  }

  if (!stats) return null;

  // Prepare data for charts
  const monthlyData = Object.entries(stats.expenses_by_month || {}).map(([month, amount]) => ({
    month,
    amount: amount,
  }));

  const categoryData = Object.entries(stats.expenses_by_category || {}).map(([category, amount]) => ({
    name: category,
    value: amount,
  }));

  const charts = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Monthly chart */}
      {monthlyData.length > 0 && (
        <div className="bg-white p-4 md:p-6 rounded-2xl md:rounded-lg shadow">
          <h2 className="text-lg md:text-xl font-semibold mb-4 text-gray-900">Expenses by month</h2>
          <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              {!isMobile && <Legend />}
              <Bar dataKey="amount" fill="#0ea5e9" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Category chart */}
      {categoryData.length > 0 && (
        <div className="bg-white p-4 md:p-6 rounded-2xl md:rounded-lg shadow">
          <h2 className="text-lg md:text-xl font-semibold mb-4 text-gray-900">Expenses by category</h2>
          <ResponsiveContainer width="100%" height={isMobile ? 240 : 300}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={isMobile ? false : ({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              {isMobile && <Legend />}
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div className="space-y-4">
        <MobileSummary stats={stats} />
        <button
          type="button"
          onClick={() => setShowCharts(v => !v)}
          className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-white border border-gray-200 text-gray-700 font-medium"
          aria-expanded={showCharts}
        >
          <BarChart3 className="w-5 h-5" />
          {showCharts ? 'Hide charts' : 'Show charts'}
        </button>
        {showCharts && charts}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title="Total expenses"
          value={`${stats.total_amount.toFixed(2)} €`}
          icon={<Euro className="w-8 h-8 text-blue-600" />}
          bgColor="bg-blue-50"
        />
        <StatCard
          title="Pending"
          value={`${stats.pending_amount.toFixed(2)} €`}
          subtitle={`${stats.pending_count} invoice(s)`}
          icon={<Clock className="w-8 h-8 text-yellow-600" />}
          bgColor="bg-yellow-50"
        />
        <StatCard
          title="Submitted"
          value={`${(stats.submitted_amount || 0).toFixed(2)} €`}
          subtitle={`${stats.submitted_count || 0} invoice(s)`}
          icon={<Send className="w-8 h-8 text-blue-500" />}
          bgColor="bg-indigo-50"
        />
        <StatCard
          title="Reimbursed"
          value={`${stats.reimbursed_amount.toFixed(2)} €`}
          subtitle={`${stats.reimbursed_count} invoice(s)`}
          icon={<CheckCircle className="w-8 h-8 text-green-600" />}
          bgColor="bg-green-50"
        />
        <StatCard
          title="Total count"
          value={stats.total_expenses}
          subtitle="invoices"
          icon={<TrendingUp className="w-8 h-8 text-purple-600" />}
          bgColor="bg-purple-50"
        />
      </div>

      {/* Graphiques */}
      {charts}

      {/* Alert if high pending amount */}
      {stats.pending_amount > 500 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <Clock className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                You have <strong>{stats.pending_amount.toFixed(2)} €</strong> pending reimbursement.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Mobile dashboard: the numbers that matter day to day. */
function MobileSummary({ stats }) {
  const navigate = useNavigate();
  const pending = usePendingExpenses();
  const [recent, setRecent] = useState([]);
  const [trip, setTrip] = useState(null); // { trip, current: bool }

  useEffect(() => {
    expenseService.getExpenses({ limit: 5 })
      .then(setRecent)
      .catch(err => console.log('Could not load recent expenses:', err));
    tripService.getTrips()
      .then(trips => {
        const today = format(new Date(), 'yyyy-MM-dd');
        const withRange = trips
          .map(t => ({ trip: t, ...tripDateRange(t) }))
          .filter(t => t.start);
        const current = withRange.find(t => today >= t.start && today <= t.end);
        const next = withRange
          .filter(t => t.start > today)
          .sort((a, b) => a.start.localeCompare(b.start))[0];
        const pick = current || next;
        if (pick) setTrip({ ...pick, current: Boolean(current) });
      })
      .catch(err => console.log('Could not load trips:', err));
  }, []);

  const monthKey = format(new Date(), 'yyyy-MM');
  const thisMonth = stats.expenses_by_month?.[monthKey] || 0;

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 rounded-2xl bg-blue-600 text-white p-4 shadow">
          <p className="text-sm text-blue-100">{format(new Date(), 'MMMM yyyy', { locale: enUS })}</p>
          <p className="text-3xl font-bold mt-1">{thisMonth.toFixed(2)} €</p>
          <p className="text-sm text-blue-100 mt-1">spent this month</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/expenses')}
          className="rounded-2xl bg-white shadow p-4 text-left"
        >
          <Clock className="w-5 h-5 text-yellow-600" />
          <p className="text-xl font-semibold text-gray-900 mt-2">{stats.pending_amount.toFixed(2)} €</p>
          <p className="text-xs text-gray-500">{stats.pending_count} not submitted</p>
        </button>
        <button
          type="button"
          onClick={() => navigate('/expenses')}
          className="rounded-2xl bg-white shadow p-4 text-left"
        >
          <Send className="w-5 h-5 text-blue-500" />
          <p className="text-xl font-semibold text-gray-900 mt-2">{(stats.submitted_amount || 0).toFixed(2)} €</p>
          <p className="text-xs text-gray-500">{stats.submitted_count || 0} awaiting reimbursement</p>
        </button>
      </div>

      {pending.length > 0 && (
        <button
          type="button"
          onClick={() => navigate('/expenses')}
          className="w-full flex items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-left"
        >
          <CloudOff className="w-5 h-5 text-amber-700" />
          <span className="flex-1 text-sm font-medium text-amber-900">
            {pending.length} receipt{pending.length > 1 ? 's' : ''} pending sync
          </span>
          <ChevronRight className="w-5 h-5 text-amber-700" />
        </button>
      )}

      {trip && (
        <button
          type="button"
          onClick={() => navigate(`/trips/${trip.trip.id}/edit`)}
          className="w-full flex items-center gap-3 rounded-2xl bg-white shadow p-4 text-left"
        >
          <span className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
            <Plane className="w-5 h-5" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-xs font-semibold uppercase tracking-wide text-purple-600">
              {trip.current ? 'Current trip' : 'Next trip'}
            </span>
            <span className="block font-medium text-gray-900 truncate">{trip.trip.name}</span>
            <span className="block text-sm text-gray-500">
              {format(parseISO(trip.start), 'd MMM', { locale: enUS })}
              {trip.end !== trip.start && ` – ${format(parseISO(trip.end), 'd MMM', { locale: enUS })}`}
            </span>
          </span>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
      )}

      {recent.length > 0 && (
        <div className="rounded-2xl bg-white shadow">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <p className="font-semibold text-gray-900">Recent expenses</p>
            <button type="button" onClick={() => navigate('/expenses')} className="text-sm text-blue-600">See all</button>
          </div>
          <div className="divide-y divide-gray-100">
            {recent.map(exp => (
              <button
                key={exp.id}
                type="button"
                onClick={() => navigate(`/expenses/${exp.id}/edit`)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left active:bg-gray-50"
              >
                <span className="min-w-0">
                  <span className="block text-gray-900 truncate">{exp.merchant || exp.category || 'Expense'}</span>
                  <span className="block text-xs text-gray-500">{format(new Date(exp.date), 'd MMM', { locale: enUS })}</span>
                </span>
                <span className="font-medium text-gray-900 whitespace-nowrap">
                  {exp.amount.toFixed(2)} <span className="text-xs text-gray-500">{exp.currency}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function StatCard({ title, value, subtitle, icon, bgColor }) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className={`flex-shrink-0 ${bgColor} rounded-md p-3`}>
            {icon}
          </div>
          <div className="ml-5 min-w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd>
                <div className="text-2xl font-semibold text-gray-900 truncate">{value}</div>
                {subtitle && (
                  <div className="text-sm text-gray-500">{subtitle}</div>
                )}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
